/**
 * Earn Defera Credit: a customer posts a video about Defera on TikTok,
 * Instagram or YouTube, submits the link (or a screenshot) on /earn, and the
 * team approves it into Defera Credit.
 *
 * This file is the ONE place the amounts and rules live. The /earn page, the
 * server's checks (server/earn-credit.js) and the AI concierge's knowledge
 * base (server/tommy-concierge.js) all read it, so they can't disagree.
 *
 * Founder calls (2026-10-09):
 *   - Video platforms only, each with a built-in paid-partnership label.
 *     Reddit is out: it bans undisclosed paid posts, and most subreddits ban
 *     promotion even when it's disclosed.
 *   - Only customers with a booked trip can earn. The FTC expects an endorser
 *     to have used the product, and it stops accounts opened just to collect
 *     credit.
 *   - Submitting licenses the post to Defera for reuse, ads included. The
 *     terms below need a lawyer's read before launch (EARN_CREDIT_ENABLED).
 *
 * Founder calls (2026-10-12):
 *   - The post, and the account it's on, stay public for 3 months (was 30
 *     days): a private profile means we can't see the post, and neither can
 *     anyone else.
 *   - Typical UGC: the customer is on camera, talking in their own voice.
 *     Slideshows, text-only videos and voiceovers over stock footage don't
 *     count. Submissions made before 2026-10-12 are reviewed under the rules
 *     they accepted (terms_version).
 *
 * Founder calls (2026-10-12, later):
 *   - $100 per video on every platform (was $150). A submission keeps the
 *     amount it was made for (amount_cents), so earlier ones still pay $150.
 *   - One submission per account per month, on any platform (was one per
 *     platform). A declined or withdrawn one doesn't use up the month, so a
 *     customer can fix it and submit again.
 *
 * The FTC side, in two rules the review must hold to:
 *   - Every post is disclosed: the platform's paid-partnership label AND #ad
 *     in the caption. A post without both is declined.
 *   - The reward never depends on what the post says. An honest, lukewarm
 *     review earns the same credit as a glowing one (the FTC's 2024 reviews
 *     and testimonials rule bans incentives conditioned on sentiment).
 *
 * No imports: the server loads this file directly.
 */

/** Bump when the rules or terms below change; each submission records it. */
export const EARN_CREDIT_TERMS_VERSION = '2026-10-12.2';

/** How long the team takes to review, as the customer reads it. */
export const EARN_CREDIT_REVIEW_DAYS = 2;
export const EARN_CREDIT_REVIEW_WINDOW = `${EARN_CREDIT_REVIEW_DAYS} business days`;

/** One submission per account in this many days (a month), on any platform. */
export const EARN_CREDIT_REPEAT_DAYS = 30;
/** The post, and the account it's on, stay public this long (3 months). */
export const EARN_CREDIT_KEEP_UP_DAYS = 90;
/** The same period as the customer reads it. */
export const EARN_CREDIT_KEEP_UP_PERIOD = '3 months';
/** The shortest video that counts. */
export const EARN_CREDIT_MIN_VIDEO_SECONDS = 15;
/** Screenshots a submission may carry. */
export const EARN_CREDIT_MAX_SCREENSHOTS = 3;

export const EARN_CREDIT_ACTIONS = [
  {
    key: 'tiktok_video',
    platform: 'TikTok',
    name: 'TikTok video',
    amountCents: 10000,
    hosts: ['tiktok.com'],
    disclosureLabel: 'Branded content',
    disclosureStep: 'Before you post, turn on Content disclosure and choose Branded content. TikTok adds a "Paid partnership" label.',
    urlHint: 'https://www.tiktok.com/@you/video/…',
  },
  {
    key: 'instagram_reel',
    platform: 'Instagram',
    name: 'Instagram Reel',
    amountCents: 10000,
    hosts: ['instagram.com'],
    disclosureLabel: 'Paid partnership',
    disclosureStep: 'Before you share, turn on Add paid partnership label (under Advanced settings).',
    urlHint: 'https://www.instagram.com/reel/…',
  },
  {
    key: 'youtube_short',
    platform: 'YouTube',
    name: 'YouTube Short',
    amountCents: 10000,
    hosts: ['youtube.com', 'youtu.be'],
    disclosureLabel: 'Paid promotion',
    disclosureStep: 'In the upload settings, tick My video contains paid promotion.',
    urlHint: 'https://youtube.com/shorts/…',
  },
];

/** What every post needs, in the order the page lists them. */
export const EARN_CREDIT_POST_REQUIREMENTS = [
  'Your own original video, posted from your own public account.',
  'You’re on camera, talking in your own voice about your own experience. Slideshows, text-only videos, screen recordings and voiceovers over stock footage don’t count.',
  `A vertical video at least ${EARN_CREDIT_MIN_VIDEO_SECONDS} seconds long, with your face clearly visible, good lighting and clear audio.`,
  'Show or say "Defera" (how you booked, how your payments work, or the trip itself).',
  "Turn on the platform's paid-partnership label and put #ad in your caption.",
  'Say what you really think. We reward the post, not the opinion; it doesn’t have to be positive.',
  `Keep the video up, and your account public, for at least ${EARN_CREDIT_KEEP_UP_PERIOD}.`,
];

/**
 * The program terms the customer accepts when submitting. Shown on /earn.
 * Needs legal review before launch.
 */
export const EARN_CREDIT_TERMS = [
  'You can earn once you have a booked Defera trip. Defera team members and contractors can’t take part.',
  `You can submit one post a month (one every ${EARN_CREDIT_REPEAT_DAYS} days), on any platform. A declined or withdrawn submission doesn’t count, so you can fix it and submit again. Each post earns once, and a repost of a video we already rewarded doesn’t earn again.`,
  `We review every submission within ${EARN_CREDIT_REVIEW_WINDOW}. If it doesn’t meet the requirements, isn’t yours, uses bought views or followers, or breaks the platform’s rules, we won’t approve it and we’ll tell you why.`,
  'Approved credit is added to your Defera Credit balance. Defera Credit never expires, goes toward trips booked with Defera, and can’t be exchanged for cash.',
  `If the post or its disclosure comes down, or your account goes private, within ${EARN_CREDIT_KEEP_UP_PERIOD}, we may remove the credit.`,
  'By submitting, you confirm the post is yours and that everyone in it agreed to be in it, and you give Defera a non-exclusive, royalty-free right to share, repost and use it, including in ads. We’ll credit you where the format allows.',
  'Defera can change or end the program at any time. Changes don’t affect credit already approved.',
];

/**
 * Ready-made reasons for declining, written to the customer: the review page
 * offers them, and whichever is picked (or typed) is what the customer reads
 * on /earn and in the email.
 */
export const EARN_CREDIT_DECLINE_REASONS = [
  'We couldn’t find the paid-partnership label and #ad on the post. Turn both on and submit it again.',
  'We couldn’t open the post. Your account may be private, or the post deleted. Make your account public and submit it again.',
  'You’re not on camera talking in the video. We need you on camera, in your own voice.',
  `The video doesn’t meet the quality the program asks for: vertical, at least ${EARN_CREDIT_MIN_VIDEO_SECONDS} seconds, your face clearly visible, good lighting and clear audio.`,
  'The post doesn’t show or mention Defera.',
  'We couldn’t confirm the post is from your account.',
  'This video was already rewarded.',
];

export function earnCreditAction(key) {
  return EARN_CREDIT_ACTIONS.find((a) => a.key === key) || null;
}

/** "$100" for whole dollars, "$12.50" otherwise. */
export function formatEarnAmount(cents) {
  const dollars = (Number(cents) || 0) / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function hostMatches(host, allowed) {
  return allowed.some((h) => host === h || host.endsWith(`.${h}`));
}

/**
 * A post link the program can check, or null. `key` identifies the post
 * itself (so the same video can't be rewarded twice under two link shapes):
 *   TikTok     /@user/video/<id>                → tiktok:<id>
 *   Instagram  /reel/<code>, /reels/<code>, /p/<code> → instagram:<code>
 *   YouTube    /shorts/<id>, /watch?v=<id>, youtu.be/<id>, /live/<id> → youtube:<id>
 * Short links (vm.tiktok.com/…) keep host + path as their key.
 */
export function normalizePostUrl(raw) {
  let text = String(raw ?? '').trim();
  if (!text) return null;
  if (!/^https?:\/\//i.test(text)) text = `https://${text}`;
  let url;
  try {
    url = new URL(text);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.toLowerCase().replace(/^(www|m|mobile)\./, '');
  if (!host.includes('.')) return null;
  const path = url.pathname.replace(/\/+$/, '');
  let key = null;
  if (hostMatches(host, ['tiktok.com'])) {
    const m = path.match(/\/video\/(\d+)/);
    key = m ? `tiktok:${m[1]}` : null;
  } else if (hostMatches(host, ['instagram.com'])) {
    const m = path.match(/^\/(?:reels?|p)\/([A-Za-z0-9_-]+)/);
    key = m ? `instagram:${m[1]}` : null;
  } else if (host === 'youtu.be') {
    const m = path.match(/^\/([A-Za-z0-9_-]{6,})/);
    key = m ? `youtube:${m[1]}` : null;
  } else if (hostMatches(host, ['youtube.com'])) {
    const m = path.match(/^\/(?:shorts|live|embed)\/([A-Za-z0-9_-]{6,})/);
    const v = url.searchParams.get('v');
    key = m ? `youtube:${m[1]}` : v && /^[A-Za-z0-9_-]{6,}$/.test(v) ? `youtube:${v}` : null;
  }
  return {
    url: url.toString(),
    host,
    key: key || `${host}${path}`,
  };
}

/** Is this link on the action's platform? */
export function postUrlMatchesAction(action, normalized) {
  return Boolean(action && normalized && hostMatches(normalized.host, action.hosts));
}

/** "@Name.Here" → "Name.Here"; null when empty or not handle-shaped. */
export function normalizeHandle(raw) {
  const handle = String(raw ?? '').trim().replace(/^@+/, '');
  if (!handle || handle.length > 60 || /\s/.test(handle)) return null;
  return handle;
}

/**
 * When a review is due: `days` business days after it was submitted,
 * skipping Saturdays and Sundays (UTC). The admin queue and Audrey's overdue
 * check both use it.
 */
export function earnReviewDueAt(submittedAt, days = EARN_CREDIT_REVIEW_DAYS) {
  const start = new Date(submittedAt);
  if (Number.isNaN(start.getTime())) return null;
  const due = new Date(start);
  let left = days;
  while (left > 0) {
    due.setUTCDate(due.getUTCDate() + 1);
    const day = due.getUTCDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  return due;
}
